import { NodeForm } from '@/components/nodes/NodeForm'
import { NodeFile } from '@/components/nodes/NodeFile'
import { type DocumentFormData } from '@/lib/validations/document'
import type { Document, Project, Task, User, Workspace } from '@/types'
import { nodeFormConfigs } from '@/lib/config/node-form-configs'

interface DocumentFormProps {
  mode: 'create' | 'edit'
  defaultValues?: Partial<DocumentFormData>
  document?: Document // For edit mode
  workspaces: Workspace[]
  projects: Project[]
  tasks: Task[]
  users: User[]
  selectedProjects: Project[]
  selectedTasks: Task[]
  selectedUsers: User[]
  onAddProject: (id: string) => void
  onRemoveProject: (id: string) => void
  onAddTask: (id: string) => void
  onRemoveTask: (id: string) => void
  onAddUser: (id: string) => void
  onRemoveUser: (id: string) => void
  onWorkspaceChange?: (value: string) => void
  uploadedFile?: File | null
  onFileChange?: (file: File | null) => void
  onSubmit: (data: DocumentFormData) => void | Promise<void>
  onCancel: () => void
  submitText: string
  cancelText?: string
  loading?: boolean
  title: string
  description: string
  onDirtyChange?: (isDirty: boolean) => void
}

export function DocumentForm({
  mode,
  defaultValues,
  document,
  workspaces,
  projects,
  tasks,
  users,
  selectedProjects,
  selectedTasks,
  selectedUsers,
  onAddProject,
  onRemoveProject,
  onAddTask,
  onRemoveTask,
  onAddUser,
  onRemoveUser,
  onWorkspaceChange,
  uploadedFile,
  onFileChange,
  onSubmit,
  onCancel,
  submitText,
  cancelText,
  loading = false,
  title,
  description,
  onDirtyChange,
}: DocumentFormProps) {
  const isEditMode = !!document

  // Convert Document data to NodeForm compatible format
  const convertToNodeFormData = (documentData?: Partial<DocumentFormData>) => {
    if (!documentData) return {}

    return {
      name: documentData.name || '',
      description: documentData.description || '',
      url: documentData.url || '',
      workspaceId: documentData.workspaceId || '',
    }
  }

  const handleNodeFormSubmit = async (data: Record<string, unknown>) => {
    const documentData = {
      ...data,
      file: uploadedFile,
    } as DocumentFormData

    await onSubmit(documentData)
  }

  // File upload component for create mode
  const FileUploadSection = () => {
    if (isEditMode || !onFileChange) return null

    return (
      <>
        <NodeFile
          uploadedFile={uploadedFile}
          onFileChange={onFileChange}
          loading={loading}
          disabled={isEditMode}
        />

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">Or provide a URL</span>
          </div>
        </div>
      </>
    )
  }

  return (
    <div className="space-y-6">
      <FileUploadSection />

      <NodeForm
        mode={mode}
        config={nodeFormConfigs.document}
        initialData={convertToNodeFormData(defaultValues)}
        externalWorkspaces={workspaces}
        externalProjects={projects}
        externalTasks={tasks}
        externalUsers={users}
        externalSelectedProjects={selectedProjects}
        externalSelectedDependencies={selectedTasks}
        externalSelectedUsers={selectedUsers}
        onExternalWorkspaceChange={onWorkspaceChange}
        onExternalProjectAdd={onAddProject}
        onExternalProjectRemove={onRemoveProject}
        onExternalTaskAdd={onAddTask}
        onExternalTaskRemove={onRemoveTask}
        onExternalUserAdd={onAddUser}
        onExternalUserRemove={onRemoveUser}
        onSubmit={handleNodeFormSubmit}
        onCancel={onCancel}
        submitText={submitText}
        cancelText={cancelText}
        loading={loading}
        title={title}
        description={description}
        onDirtyChange={onDirtyChange}
      />
    </div>
  )
}
